import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import {LoadingManager} from 'three';
import {BlobReader, BlobWriter, ZipReader} from "@zip.js/zip.js/index.cjs";
import * as iconv from 'iconv-lite';
import clone from "lodash.clonedeep";

export const TYPE_PRODUCE = 4;
export const TYPE_INJECT = 5;
export const TYPE_CLOSED = 6;
export const TYPE_IMAGINARY = 7;

export async function extractZipFile(zipBlob) {
    const zipFileReader = new BlobReader(zipBlob);
    const zipReader = new ZipReader(zipFileReader);
    let entries = await zipReader.getEntries();
    const fileMap = new Map();
    if (entries && entries.length > 0) {
        for (let entry of entries) {
            let blob = await entry.getData(new BlobWriter());
            let filename = entry.filename.replace(/.*\//, '');
            fileMap.set(filename, blob);
        }
    }

    await zipReader.close();
    return fileMap;
}

export function parseGltf(gltfData) {
    return new Promise( (resolve, reject) => {
        extractZipFile(gltfData).then((assetMap) => {
            let blobURLs = [];
            const manager = new LoadingManager();
            manager.setURLModifier((url, path) => {
                const normalizedURL = decodeURI(url)
                    .replace(/^(\.?\/)/, '');

                if (assetMap.has(normalizedURL)) {
                    const blob = assetMap.get(normalizedURL);
                    const blobURL = URL.createObjectURL(blob);
                    blobURLs.push(blobURL);
                    return blobURL;
                }
                return (path || '') + url;
            });

            const loader = new GLTFLoader(manager);
            const gltfModelBlob = assetMap.get('model.gltf');

            const reader = new FileReader();
            reader.addEventListener('loadend', (e) => {
                const gltfModelJson = e.target.result;
                loader.parse(gltfModelJson, '/', (gltf) => {
                    blobURLs.map(URL.revokeObjectURL);
                    resolve(gltf.scene);
                }, reject);
            });
            reader.readAsText(gltfModelBlob);
        });
    });
}

export function removeBOM(text) {
    return text
        .replace(/^\uFEFF/g, "")
        .replace(/^\u00BB\u00BF/g,"")
        .replace(/^\xEF\xBB\xBF/g, "");
}

export function trimCells(rows) {
    let trimmedRows = [];
    for (let row of rows) {
        let trimmedRow = [];
        for (let cell of row) {
            trimmedRow.push( cell.trim() );
        }

        trimmedRows.push(trimmedRow);
    }

    return trimmedRows;
}

export function parseCsvFile(text, separator = ',') {
    //https://stackoverflow.com/a/41563966
    let prevChar = '';
    let row = [''];
    let result = [row];
    let cellIndex = 0;
    let rowIndex = 0;
    let isSeparatorOpen = true;
    let char = null;

    text = removeBOM(text);
    let onlyEnglishChars = text.search(/^[a-z0-9"',;.\s]+$/i) === 0;
    let hasRussianUtf8Chars = text.search(/[а-яё]+/ui) !== -1;
    let isWrongEncoding = !onlyEnglishChars && !hasRussianUtf8Chars;
    if (isWrongEncoding) {
        text = iconv.decode(text, 'win1251', {stripBOM: true});
    }

    for (char of text) {
        if (char === '"') {
            if (isSeparatorOpen && char === prevChar) {
                row[cellIndex] += char;
            }
            isSeparatorOpen = !isSeparatorOpen;
        }
        else if (separator === char && isSeparatorOpen) {
            ++cellIndex;
            char = '';
            row[cellIndex] = '';
        }
        else if (char === '\n' && isSeparatorOpen) {
            if (prevChar === '\r') {
                row[cellIndex] = row[cellIndex].slice(0, -1);
            }

            ++rowIndex;
            char = '';
            row = [''];
            result[rowIndex] = row;
            cellIndex = 0;
        }
        else {
            row[cellIndex] += char;
        }
        prevChar = char;
    }

    result = result.filter(row => {
        let isEmptyRow = row.length === 0 || (row.length === 1 && row[0] === '');
        return !isEmptyRow;
    });

    return trimCells(result);
}

export function padArray(values, length, defaultValue = 0) {
    let padded = Array(length).fill(defaultValue);
    for (let index in values) {
        padded[index] = values[index];
    }

    return padded;
}

export function arrayToFloat(values) {
    return values.map(num => parseFloat(num));
}

export function arrayToInt(values) {
    return values.map(num => parseInt(num));
}

export function arrayBufferToString(buffer){
    let arr = new Uint8Array(buffer);
    let str = '';
    for (let code of arr) {
        str += String.fromCharCode(code);
    }

    return str;
}

export function blobToFile(theBlob, fileName){
    return new File([theBlob], fileName, { lastModified: new Date().getTime(), type: theBlob.type })
}

export function getFileContent(file) {
    return new Promise(resolve => {
        if (file.content) {
            return resolve(file.content);
        }

        if (file.buffer) {
            return resolve(arrayBufferToString(file.buffer));
        }

        const reader = new FileReader();
        reader.addEventListener('loadend', event => {
            resolve(event.target.result);
        });
        reader.readAsText(file);
    });
}

export function ijkToXyz(i, j, k, boxSize) {
    let boxShift = 0.5;
    let shiftX = boxShift;
    let shiftY = boxShift;
    let shiftZ = boxShift;

    let x = (i+shiftX) * boxSize[0];
    let y = (j+shiftY) * boxSize[1];
    let z = (k+shiftZ) * boxSize[2];

    return [x, y, z];
}

export function xyzToIjk(x, y, z, boxSize, boxShift=0, scale = 1) {
    if (!(boxShift instanceof Array)) {
        boxShift = [boxShift, boxShift, boxShift];
    }

    let [shiftX, shiftY, shiftZ] = boxShift;
    let [xSize, ySize, zSize] = boxSize;

    let i = Math.floor(x / (xSize * scale) - shiftX);
    let j = Math.floor(y / (ySize * scale) - shiftY);
    let k = Math.floor(z / (zSize * scale) - shiftZ);

    return [i, j, k];
}

export function HSVtoRGB(h, s, v) {
    let r, g, b, i, f, p, q, t;
    if (arguments.length === 1) {
        s = h.s; v = h.v; h = h.h;
    }
    i = Math.floor(h * 6);
    f = h * 6 - i;
    p = v * (1 - s);
    q = v * (1 - f * s);
    t = v * (1 - (1 - f) * s);
    switch (i % 6) {
        case 0: r = v; g = t; b = p; break;
        case 1: r = q; g = v; b = p; break;
        case 2: r = p; g = v; b = t; break;
        case 3: r = p; g = q; b = v; break;
        case 4: r = t; g = p; b = v; break;
        case 5: r = v; g = p; b = q; break;
    }
    return {
        r: Math.round(r * 255),
        g: Math.round(g * 255),
        b: Math.round(b * 255)
    };
}

export function pointDistance(p1, p2) {
    let dx = p1.x - p2.x;
    let dy = p1.y - p2.y;
    let dz = p1.z - p2.z;
    return Math.sqrt(Math.pow(dx, 2) + Math.pow(dy, 2) + Math.pow(dz, 2));
}

export function randomId() {
    return Math.random().toString(36).slice(-6);
}

export function setMaterial(node, material) {
    node.material = material;
    if (node.children) {
        for (var i = 0; i < node.children.length; i++) {
            setMaterial(node.children[i], material);
        }
    }
}

export function truncate(str, n){
    if (!str) {
        return '';
    }

    return (str.length > n) ? str.substr(0, n-1) + '...' : str;
}

export function getWellXYZ(well, grid = null) {
    if (!well || !well.x_coord) {
        return null;
    }

    let x = parseFloat(well.x_coord);
    let y = parseFloat(well.y_coord);
    let z = parseFloat(well.z_coord_relative);

    if (grid) {
        let gridHeight = grid.size[2] * grid.cell_size[2];
        z = gridHeight - z;
    }

    return [x, y, z]
}

export function setWellXYZ(well, [x, y, z], grid = null) {
    if (grid) {
        let gridHeight = grid.size[2] * grid.cell_size[2];
        z = gridHeight - z;
    }

    let dz = well.z_coord - well.z_coord_relative;
    well.x_coord = parseFloat(x);
    well.y_coord = parseFloat(y);
    well.z_coord = parseFloat(z) + dz;
    well.z_coord_relative = parseFloat(z);

    return well;
}

export function getNodeLayers(nodes, cellHeight = 1) {
    let points = nodes.map(node => [node.x_coord, node.y_coord, node.z_coord_relative]);
    let ks = points.map(point => Math.floor(point[2] / cellHeight));
    let uniqueKs = Array.from(new Set(ks));
    let layers = uniqueKs.map(k => ks.reduce((points, currentK, index) => {
        if (k === currentK) {
            points.push(nodes[index]);
        }
        return points;
    }, []));

    return layers;
}

export function downloadJson(item) {
    let downloadItem = clone(item);
    if (item?.problem) {
        downloadItem = item.problem;
    }

    delete downloadItem['uploadedFiles'];

    let blob = new Blob([JSON.stringify(downloadItem, undefined, 2)], { type: "text/json" });
    let link = document.createElement("a");

    link.download = (item.name || 'model') + '.json';
    link.href = window.URL.createObjectURL(blob);
    link.dataset.downloadurl = ["text/json", link.download, link.href].join(":");

    let evt = new MouseEvent("click", {
        view: window,
        bubbles: true,
        cancelable: true,
    });

    link.dispatchEvent(evt);
    link.remove()
}

export function findNodeIndex(searchNode, wellId = null, nodes) {
    let searchWellId = wellId ? wellId : searchNode.well_id;

    for (let index = 0; index < nodes.length; index++) {
        let node = nodes[index];
        if (searchWellId && node.well_id !== searchWellId) {
            continue;
        }

        if (
            node.x_coord === searchNode.x_coord &&
            node.y_coord === searchNode.y_coord &&
            node.z_coord === searchNode.z_coord
        ) {
            return index;
        }
    }

    return null;
}

export function getWellConnections(well, connections, nodes) {
    if (!well?.nodes) {
        return [];
    }

    let nodeIndexes = well.nodes
        .map(node => findNodeIndex(node, well.id, nodes))
        .filter(index => index !== null);

    return connections
        .filter(connection => {
            return nodeIndexes.includes(connection[0]) || nodeIndexes.includes(connection[1]);
        });
}

export function getConnectionsValue(well, connections, nodes) {
    return getWellConnections(well, connections, nodes)
        .map(connection => {
            return JSON.stringify(connection);
        });
}

export function getConnectionValue(fromIndex, toIndex) {
    if (fromIndex > toIndex) {
        return [toIndex, fromIndex];
    }
    else {
        return [fromIndex, toIndex];
    }
}

export function getAllConnectionsText(fromWell, wells, nodes) {
    if (!(fromWell?.nodes?.length > 0)) {
        return [];
    }

    let allConnections = [];

    for (let fromWellNodeIndex = 0; fromWellNodeIndex < fromWell.nodes.length; fromWellNodeIndex++) {
        let fromNode = fromWell.nodes[fromWellNodeIndex];
        let fromNodeIndex = findNodeIndex(fromNode, fromWell.id, nodes);
        if (fromNodeIndex === null) {
            continue;
        }

        for (let toWell of wells) {
            if (fromWell.id === toWell.id) {
                continue;
            }

            for (let toWellNodeIndex = 0; toWellNodeIndex < toWell.nodes.length; toWellNodeIndex++) {
                let toNode = toWell.nodes[toWellNodeIndex];

                let toNodeIndex = findNodeIndex(toNode, toWell.id, nodes);
                if (toNodeIndex !== null) {
                    let titleTemplate;
                    if (fromWell.nodes.length > 1) {
                        if (toWell.nodes.length > 1) {
                            titleTemplate = 'перф. #:fromIndex: -> :well_id:, перф. #:toIndex:';
                        }
                        else {
                            titleTemplate = 'перф. #:fromIndex: -> :well_id:';
                        }
                    }
                    else {
                        if (toWell.nodes.length > 1) {
                            titleTemplate = ':well_id:, перф. #:toIndex:';
                        }
                        else {
                            titleTemplate = ':well_id:';
                        }
                    }

                    let text = titleTemplate
                        .replace(':fromIndex:', (fromWellNodeIndex + 1))
                        .replace(':toIndex:', (toWellNodeIndex+1))
                        .replace(':well_id:', toWell.id);

                    if (fromNode.z_coord_relative < toNode.z_coord_relative) {
                        text += ' ↑';
                    }
                    else if (fromNode.z_coord_relative > toNode.z_coord_relative) {
                        text += ' ↓';
                    }
                    else {
                        text += ' =';
                    }

                    let value = getConnectionValue(fromNodeIndex, toNodeIndex);
                    allConnections.push({
                        text,
                        value: JSON.stringify(value),
                        connection: value
                    });
                }
            }
        }
    }

    return allConnections;
}

export function editDistance(s1, s2) {
    s1 = s1.toLowerCase();
    s2 = s2.toLowerCase();

    let costs = [];
    for (let i = 0; i <= s1.length; i++) {
        let lastValue = i;
        for (let j = 0; j <= s2.length; j++) {
            if (i === 0) {
                costs[j] = j;
            }
            else {
                if (j > 0) {
                    let newValue = costs[j - 1];
                    if (s1.charAt(i - 1) !== s2.charAt(j - 1)) {
                        newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
                    }
                    costs[j - 1] = lastValue;
                    lastValue = newValue;
                }
            }
        }
        if (i > 0) {
            costs[s2.length] = lastValue;
        }
    }
    return costs[s2.length];
}
